import { useCallback, useState } from 'react';
import { useSecureService } from '../store/service';
import {
  type DownloadItemsFetchers,
  type DownloadLinkItem,
  getDownloadItemsForEntry,
  triggerDownloadsFromItems,
} from '../utils/downloadFromLinks';

export type DownloadableEntry = {
  id: number;
  path?: string;
  entryType?: string;
};

export function useEntryDownloadProgress() {
  const { getFileById, getDownloadLinks } = useSecureService();
  const [progressById, setProgressById] = useState<Record<number, number>>({});

  const setIdsProgress = useCallback((ids: number[], percent: number) => {
    setProgressById((prev) => {
      const next = { ...prev };
      ids.forEach((id) => {
        next[id] = percent;
      });
      return next;
    });
  }, []);

  const clearIdsProgress = useCallback((ids: number[]) => {
    window.setTimeout(() => {
      setProgressById((prev) => {
        const next = { ...prev };
        ids.forEach((id) => {
          delete next[id];
        });
        return next;
      });
    }, 500);
  }, []);

  const runDownload = useCallback(
    async (entries: DownloadableEntry[]) => {
      if (entries.length === 0) return;

      const ids = entries.map((e) => e.id);
      const fetchers: DownloadItemsFetchers = {
        getFileById,
        getDownloadLinks: getDownloadLinks as DownloadItemsFetchers['getDownloadLinks'],
      };

      setIdsProgress(ids, 0);

      try {
        let totalItems: DownloadLinkItem[] = [];
        for (const entry of entries) {
          const items = await getDownloadItemsForEntry(entry, fetchers);
          totalItems = totalItems.concat(items);
        }

        if (totalItems.length === 0) {
          setProgressById((prev) => {
            const next = { ...prev };
            ids.forEach((id) => {
              delete next[id];
            });
            return next;
          });
          return;
        }

        await triggerDownloadsFromItems(totalItems, {
          onProgress: ({ percent }) => setIdsProgress(ids, percent),
        });
        setIdsProgress(ids, 100);
      } finally {
        clearIdsProgress(ids);
      }
    },
    [clearIdsProgress, getDownloadLinks, getFileById, setIdsProgress]
  );

  const downloadEntry = useCallback(
    (entry: DownloadableEntry) => runDownload([entry]),
    [runDownload]
  );

  const downloadEntries = useCallback(
    (entries: DownloadableEntry[]) => runDownload(entries),
    [runDownload]
  );

  const getCardDownloadProgress = useCallback(
    (id: number) => (id in progressById ? progressById[id] : undefined),
    [progressById]
  );

  return {
    progressById,
    getCardDownloadProgress,
    downloadEntry,
    downloadEntries,
  };
}
